import React, { useState, useEffect } from "react";
import ApplicationForm from "./ApplicationForm";
import { getFirestore, collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import app, { defaultAuth, getCloudFunctionUrl, studentAuth } from "../../firebase";
import { Link, useNavigate } from "react-router-dom";
import { signInWithCustomToken, signOut } from "firebase/auth";
import { useSession } from "../../context/SessionContext";
import { validateActiveAccount } from "../../authStatus";
import LoginErrorMessage from "../common/LoginErrorMessage";
import { assertRoleLoginAllowed, readNewStudentSession, useRoleSessionState, writeNewStudentSession } from "../../auth/roleSession";


const localStorage = window.sessionStorage;
const STUDENT_PORTAL_SIGN_IN_URL = getCloudFunctionUrl("studentPortalSignIn");

export default function SignInPage() {
  const navigate = useNavigate();
  const { loading } = useSession();
  const { loading: studentAuthLoading, user: studentAuthUser } = useRoleSessionState("New Student");
  const [showConsent, setShowConsent] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [idNumber, setIdNumber] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const db = getFirestore(app);

  // Check for auto-redirect on mount AND when localStorage changes
  useEffect(() => {
    const checkAndRedirect = async () => {
      if (loading || studentAuthLoading) return;
      setCheckingSession(true);
      let storedStudent = readNewStudentSession();
      let isStudentLoggedIn = localStorage.getItem("studentPortalLogin") === "true";

      if (!storedStudent && studentAuthUser) {
        let studentSnapshot;
        try {
          studentSnapshot = await getDocs(query(
            collection(db, "students"),
            where("uid", "==", studentAuthUser.uid)
          ));
        } catch (error) {
          console.error("Could not restore the authenticated student profile.", error);
          setLoginError("Could not verify your account. Please sign in again.");
          setCheckingSession(false);
          return false;
        }
        if (!studentSnapshot.empty) {
          writeNewStudentSession({ id: studentSnapshot.docs[0].id, ...studentSnapshot.docs[0].data(), uid: studentAuthUser.uid });
          storedStudent = readNewStudentSession();
          localStorage.setItem("studentPortalLogin", "true");
          isStudentLoggedIn = true;
        }
      }
      
      if (storedStudent && isStudentLoggedIn) {
        try {
          const student = storedStudent;
          if (!studentAuthUser || !student.id || !student.firstName || !student.lastName) {
            localStorage.removeItem("studentPortalLogin");
            localStorage.removeItem("currentStudentSession");
            setCheckingSession(false);
            return false;
          }
          const studentSnapshot = await getDoc(doc(db, "students", student.id));
          if (!studentSnapshot.exists() || studentSnapshot.data().uid !== studentAuthUser.uid) {
            localStorage.removeItem("studentPortalLogin");
            localStorage.removeItem("currentStudentSession");
            setLoginError("Your account could not be verified. Please sign in again.");
            setCheckingSession(false);
            return false;
          }
          const validation = validateActiveAccount(studentSnapshot.data(), "Student account");
          if (!validation.active) {
            localStorage.removeItem("studentPortalLogin");
            localStorage.removeItem("currentStudentSession");
            setLoginError(validation.message);
            setCheckingSession(false);
            return false;
          }
          const nameSlug = `${student.firstName}-${student.lastName}`.replace(/\s+/g, "-").toLowerCase();
          navigate(`/student-portal/dashboard/${nameSlug}`, { state: { student }, replace: true });
          return true;
        } catch (e) {
          console.error("[StudentPortal] Error parsing stored student:", e);
          localStorage.removeItem("studentPortalLogin");
          localStorage.removeItem("currentStudentSession");
        }
      }
      setCheckingSession(false);
      return false; // No redirect
    };
    
    // Check on mount
    checkAndRedirect();
    
    // Also listen for storage changes from other tabs or same tab
    const handleStorageChange = (e) => {
      if (e.key === "studentPortalLogin" || e.key === "currentStudentSession") {
        console.log("[StudentPortal] Storage changed, checking redirect:", { key: e.key, newValue: e.newValue ? "set" : "cleared" });
        checkAndRedirect();
      }
    };
    
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [db, loading, navigate, studentAuthLoading, studentAuthUser]);
  // Map Firebase auth errors to user-friendly messages
  const getFriendlyAuthError = (err) => {
    if (!err) return "Login failed. Please try again.";
    const code = err.code || "";
    if (code === "auth/wrong-password") return "Incorrect password. Try again or use 'Forgot login password?'.";
    if (code === "auth/user-not-found") return "Account not found. Please check your ID or create an account.";
    if (code === "auth/invalid-email") return "Invalid email address format.";
    if (code === "auth/invalid-credential") return "Invalid credentials. Please check your ID and password.";
    if (code === "auth/too-many-requests") return "Too many failed attempts. Please try again later or reset your password.";
    if (code === "auth/network-request-failed") return "Network error. Check your connection and try again.";
    // Fallback: inspect message for known tokens
    const msg = (err.message || "").toLowerCase();
    if (msg.includes("invalid-credential")) return "Invalid credentials. Please check your ID and password.";
    if (msg.includes("wrong-password")) return "Incorrect password. Try again or use 'Forgot login password?'.";
    return err.message || "Login failed. Please check your credentials and network.";
  };

  async function signInWithAuth(e) {
    e?.preventDefault();
    setLoginError("");
    setLoggingIn(true);

    if (!(idNumber || "").trim()) {
      setLoginError("Enter your ID number.");
      setLoggingIn(false);
      return;
    }
    if (!(password || "").trim()) {
      setLoginError("Enter your password.");
      setLoggingIn(false);
      return;
    }

    let createdSessions = false;
    try {
      const response = await fetch(STUDENT_PORTAL_SIGN_IN_URL, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({idNumber: idNumber.trim(), password})
      });
      const result = await response.json().catch(() => null);
      if (
        !response.ok ||
        typeof result?.customToken !== "string" ||
        typeof result?.uid !== "string" ||
        typeof result?.studentDocumentId !== "string"
      ) {
        setLoginError(result?.error?.message || "Invalid ID number or password.");
        return;
      }

      await assertRoleLoginAllowed("New Student", result.uid);
      const [defaultCredential, studentCredential] = await Promise.all([
        signInWithCustomToken(defaultAuth, result.customToken),
        signInWithCustomToken(studentAuth, result.customToken)
      ]);
      createdSessions = true;
      if (defaultCredential.user.uid !== studentCredential.user.uid) {
        throw new Error("The authenticated student account could not be verified.");
      }
      const studentSnapshot = await getDoc(doc(db, "students", result.studentDocumentId));
      if (!studentSnapshot.exists() || studentSnapshot.data().uid !== defaultCredential.user.uid) {
        throw new Error("The student profile could not be verified.");
      }
      const stu = studentSnapshot.data();
      const validation = validateActiveAccount(stu, "Student account");
      if (!validation.active) throw new Error(validation.message);
      if (!stu.firstName || !stu.lastName) {
        throw new Error("Student record incomplete. Contact the administrator.");
      }

      const finalStudent = {
        ...stu,
        id: result.studentDocumentId,
        uid: defaultCredential.user.uid
      };
      delete finalStudent.password;
      delete finalStudent.studentpassword;
      
      // Store flags BEFORE navigation to prevent race conditions
      localStorage.setItem("studentPortalLogin", "true");
      writeNewStudentSession(finalStudent);
      const nameSlug = `${finalStudent.firstName}-${finalStudent.lastName}`.replace(/\s+/g, "-").toLowerCase();
      navigate(`/student-portal/dashboard/${nameSlug}`, { state: { student: finalStudent }, replace: true });
    } catch (err) {
      if (createdSessions) {
        await Promise.all([signOut(defaultAuth), signOut(studentAuth)]).catch(cleanupError => {
          console.error("Failed to clean up unsuccessful student sign-in.", cleanupError);
        });
      }
      setLoginError(getFriendlyAuthError(err));
    } finally {
      setLoggingIn(false);
    }
  }



  if (showApplicationForm) {
    return <ApplicationForm onClose={() => setShowApplicationForm(false)} />;
  }

  // Show loading only while actively checking Firebase
  if (checkingSession) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(90deg, #009966 0%, #155c2b 100%)"
      }}>
        <div style={{ color: "#fff", fontSize: 18, fontWeight: 500 }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(90deg, #009966 0%, #155c2b 100%)"
    }}>
      {/* Right Login Card */}
      <div style={{ position: "relative", width: 520, maxWidth: "calc(100% - 24px)", background: "#fff", borderRadius: 24, boxShadow: "0 4px 24px rgba(0,0,0,0.10)", padding: 40, boxSizing: "border-box", zIndex: 2 }}>
        <h2 style={{ textAlign: "center", fontWeight: 700, color: "#155c2b", marginBottom: 32, letterSpacing: 2 }}>ENROLLEASE</h2>
        <div style={{ marginBottom: 18 }}>
          <label style={{ fontWeight: 500, color: "#155c2b" }}>ID Number</label>
          <div style={{ position: "relative" }}>
            <input
              type="text"
              placeholder="Enter your id number"
              value={idNumber}
              onChange={e => setIdNumber(e.target.value)}
              style={{ width: "100%", padding: "12px 40px 12px 12px", borderRadius: 8, border: "1px solid #ccc", marginTop: 6, boxSizing: "border-box" }}
              autoComplete="username"
            />
            <span style={{ position: "absolute", right: 12, top: 14, color: "#888" }}>
              <i className="fas fa-id-card"></i>
            </span>
          </div>
        </div>
        <div style={{ marginBottom: 18 }}>
          <label style={{ fontWeight: 500, color: "#155c2b" }}>Password</label>
          <div style={{ position: "relative" }}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{ width: "100%", padding: "12px 40px 12px 12px", borderRadius: 8, border: "1px solid #ccc", marginTop: 6, boxSizing: "border-box" }}
              autoComplete="current-password"
            />
            <button
              type="button"
              title={showPassword ? "Hide password" : "Show password"}
              aria-label={showPassword ? "Hide password" : "Show password"}
              style={{ position: "absolute", right: 8, top: 10, width: 32, height: 32, padding: 0, border: 0, background: "transparent", color: "#155c2b", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
              onClick={() => setShowPassword(value => !value)}
            >
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />
                {showPassword ? <path d="m4 4 16 16" /> : <circle cx="12" cy="12" r="2.5" />}
              </svg>
            </button>
          </div>
        </div>
        <LoginErrorMessage message={loginError} />
        <div style={{ textAlign: "right", marginBottom: 18 }}>
          <Link
            to="/forgot-password?scope=New%20Student&returnTo=%2Fsignin"
            style={{ color: "#e67e22", fontSize: 14, textDecoration: "none" }}
          >
            Forgot Password?
          </Link>
        </div>
        <button
          style={{ width: "100%", padding: "12px 0", background: loggingIn ? "#ccc" : "#155c2b", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 18, marginBottom: 18 }}
          disabled={loggingIn}
          onClick={signInWithAuth}
        >
          <i className="fas fa-sign-in-alt" style={{ marginRight: 8 }}></i> {loggingIn ? "Logging in..." : "LOG IN"}
        </button>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
          <button
            style={{ background: "#fff", color: "#e67e22", border: "1px solid #e67e22", borderRadius: 6, padding: "6px 18px", fontWeight: 500, fontSize: 15 }}
            onClick={() => setShowConsent(true)}
          >
            APPLY NOW
          </button>
          <a
            href="https://online.mac.edu.ph/static/custom/AIMS-USER-GUIDE.pdf"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: "#fff",
              color: "#e67e22",
              border: "1px solid #e67e22",
              borderRadius: 6,
              padding: "6px 18px",
              fontWeight: 500,
              fontSize: 15,
              textDecoration: "none",
              display: "inline-block"
            }}
          >
            ENROLLMENT GUIDE
          </a>
        </div>
        <div style={{ background: "#f5f5f5", borderRadius: 8, padding: 14, fontSize: 14, color: "#155c2b" }}>
          For inquiries and concerns, contact us:<br />
          <span style={{ fontWeight: 600 }}>ADMISSION AND RECORDS OFFICE</span><br />
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <i className="fas fa-phone-alt"></i>Trunkline (632) 8525 9191 to 98 local 282
          </span><br />
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <i className="fas fa-envelope"></i> registrar@mac.edu.ph
          </span>
        </div>
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <Link to="/" style={{ color: "#e67e22", fontSize: 14, textDecoration: "none" }}>Return to home</Link>
        </div>
      </div>
      {/* Consent modal and others (existing) */}
      {showConsent && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(0,0,0,0.25)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <div style={{
            background: "#fff",
            borderRadius: 12,
            maxWidth: 520,
            width: "98%",
            minHeight: 600,
            padding: 32,
            boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
            fontSize: 15,
            color: "#222",
            position: "relative"
          }}>
            <h3 style={{ fontWeight: 700, fontSize: 22, marginBottom: 18 }}>Terms and Conditions on Data Collection Policy</h3>
            <div style={{ maxHeight: 420, overflowY: "auto", marginBottom: 18, fontSize: 14, lineHeight: 1.6 }}>
              <p>I hereby attest that all information stated in this form is true and correct to the best of my knowledge. I understand that any concealment, false statement, and/or non-disclosure shall constitute fraud and may be grounds for legal action against me. I waive my right to institute any case arising from this situation.</p>
              <p>I have read this form, understood its contents, and consent to the processing of my personal data. I understand that my consent does not preclude the existence of other lawful bases for processing personal data and does not waive any of my rights under the Data Privacy Act of 2012 and other applicable laws.</p>
              <p>I have provided the information herein after being informed of the purpose of its processing, and I expressly give my consent thereto. I understand that it is my choice what information I provide and that withholding or falsifying information may adversely affect my relationship with Manila Adventist College. I am aware that I may request access to my personal information and, if necessary, correct any information I believe to be inaccurate. I understand that if, in exceptional circumstances, access is denied for legitimate purposes, I will be informed of the reason and the available remedies.</p>
              <p>Furthermore, I warrant that I have:</p>
              <p style={{ marginLeft: 20 }}>(i) obtained consent from third persons, if any, to disclose their information included in this form; and</p>
              <p style={{ marginLeft: 20 }}>(ii) informed said third persons of the purpose for the disclosure and collection of their information.</p>
              <p>I shall indemnify and hold Manila Adventist College free and harmless from any and all claims arising from the breach of this warranty, including damages and legal fees incurred in defending such claims.</p>
              <p>Unless otherwise provided by law or by appropriate College policies, this consent shall be retained indefinitely for documentation purposes. Where a retention period is provided by law and/or College policy, all affected records shall be securely disposed of after such period.</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 18 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  id="termsCheck"
                  checked={consentChecked}
                  onChange={e => setConsentChecked(e.target.checked)}
                  style={{ marginRight: 8 }}
                />
                I agree to the Terms and Conditions for this application.
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  id="privacyCheck"
                  checked={privacyConsent}
                  onChange={e => setPrivacyConsent(e.target.checked)}
                  style={{ marginRight: 8 }}
                />
                I consent to the collection and processing of my personal data under the Data Privacy Policy.
              </label>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
              <button
                onClick={() => {
                  setShowConsent(false);
                  setConsentChecked(false);
                  setPrivacyConsent(false);
                }}
                style={{ background: "#f5f5f5", color: "#e67e22", border: "1px solid #e0e0e0", borderRadius: 6, padding: "8px 22px", fontWeight: 500, fontSize: 15 }}
              >
                CLOSE
              </button>
              <button
                disabled={!consentChecked || !privacyConsent}
                style={{ background: (consentChecked && privacyConsent) ? "#009966" : "#eee", color: (consentChecked && privacyConsent) ? "#fff" : "#888", border: "none", borderRadius: 6, padding: "8px 22px", fontWeight: 500, fontSize: 15, cursor: (consentChecked && privacyConsent) ? "pointer" : "not-allowed" }}
                onClick={() => {
                  if (consentChecked && privacyConsent) {
                    setShowConsent(false);
                    setShowApplicationForm(true);
                  }
                }}
              >
                YES, I AGREE
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
